/**
 * The AI Search panel: the corpora its tabs search, and the search that the agent service reads
 * out of the operator's text (POST /agent/api/v1/search/parse, `searchparse.Result` in Go).
 * Also the archive moment search of one camera, which the media_server answers as an NDJSON
 * stream (GET /relay/proxy/<serverId>/api/v1/devices/<id>/ai-search, `SearchResult` in C++).
 */

import { z } from 'zod';

import { EventSeverity } from './events';
import type { TimeWindow } from './filters';

/** The corpus of a tab. Frozen: the value crosses the JSON boundary in both directions. */
export const AiSearchTab = {
    Objects: 'objects',
    Events: 'events',
    Video: 'video',
} as const;
export type AiSearchTab = (typeof AiSearchTab)[keyof typeof AiSearchTab];

/**
 * The tabs of the panel: the request field, then one for each corpus. Frozen: the panel reads
 * the tab to open from its local props, which are JSON.
 */
export const AiSearchPanelTab = { Ask: 'ask', ...AiSearchTab } as const;
export type AiSearchPanelTab = (typeof AiSearchPanelTab)[keyof typeof AiSearchPanelTab];

/** The classes of object that the parse names, as the model writes them. */
export const AiObjectType = {
    Person: 'person',
    Vehicle: 'vehicle',
    Car: 'car',
    Truck: 'truck',
    Bus: 'bus',
    Motorcycle: 'motorcycle',
    Bicycle: 'bicycle',
} as const;
export type AiObjectType = (typeof AiObjectType)[keyof typeof AiObjectType];

/** The kinds of event that the parse names. */
export const AiEventType = {
    Insights: 'insights',
    Notifications: 'notifications',
} as const;
export type AiEventType = (typeof AiEventType)[keyof typeof AiEventType];

/** What the panel sends: the text, the operator's zone, the tab on screen and every camera. */
export interface SearchParseRequest {
    text: string;
    /** An IANA zone id: the parse reads "today" and clock times in it. */
    timeZone: string;
    tab: AiSearchTab;
    cameras: { id: string; name: string }[];
}

/**
 * The search that the text asks for. A field that is absent leaves the panel's filter as the
 * operator set it.
 */
export const SearchParseResultSchema = z.object({
    searchType: z.enum([AiSearchTab.Objects, AiSearchTab.Events, AiSearchTab.Video]),
    query: z.string(),
    cameraIds: z.array(z.string()).optional(),
    startMs: z.number().optional(),
    endMs: z.number().optional(),
    objectType: z.enum([
        AiObjectType.Person, AiObjectType.Vehicle, AiObjectType.Car, AiObjectType.Truck,
        AiObjectType.Bus, AiObjectType.Motorcycle, AiObjectType.Bicycle,
    ]).optional(),
    /** What the object hits of `query` must not look like. */
    negativeText: z.string().optional(),
    eventType: z.enum([AiEventType.Insights, AiEventType.Notifications]).optional(),
    minSeverity: z.enum([
        EventSeverity.Lowest, EventSeverity.Low, EventSeverity.Normal, EventSeverity.High,
    ]).optional(),
});
export type SearchParseResult = z.infer<typeof SearchParseResultSchema>;

/** How the archive moment search ended; `inProgress` marks an interval update on the way. */
export const AiSearchStatus = {
    InProgress: 'inProgress',
    Found: 'found',
    NoChangeFound: 'noChangeFound',
    DeadlineExceeded: 'deadlineExceeded',
    ProbeFailed: 'probeFailed',
    NoRecording: 'noRecording',
    Stopped: 'stopped',
} as const;
export type AiSearchStatus = (typeof AiSearchStatus)[keyof typeof AiSearchStatus];

/** The way the area's answer changed over the found interval. */
export const AiSearchDirection = {
    None: 'none',
    Appeared: 'appeared',
    Disappeared: 'disappeared',
} as const;
export type AiSearchDirection = (typeof AiSearchDirection)[keyof typeof AiSearchDirection];

/**
 * One NDJSON line of the archive moment search. A line with status `inProgress` narrows the
 * interval; any other status is the final line, whose interval brackets the transition.
 */
export const AiSearchResultSchema = z.object({
    status: z.enum([
        AiSearchStatus.InProgress, AiSearchStatus.Found, AiSearchStatus.NoChangeFound,
        AiSearchStatus.DeadlineExceeded, AiSearchStatus.ProbeFailed, AiSearchStatus.NoRecording,
        AiSearchStatus.Stopped,
    ]),
    intervalStartMs: z.number(),
    intervalEndMs: z.number(),
    probes: z.number(),
    elapsedMs: z.number(),
    direction: z.enum([
        AiSearchDirection.None, AiSearchDirection.Appeared, AiSearchDirection.Disappeared,
    ]),
    answerAtStart: z.boolean().optional(),
    answerAtEnd: z.boolean().optional(),
    /** The cause, for the failure statuses; empty otherwise. */
    error: z.string(),
});
export type AiSearchResult = z.infer<typeof AiSearchResultSchema>;

/** The panel's search: the tab, the query and each filter. */
export interface SearchState {
    tab: AiSearchTab;
    query: string;
    cameraIds: string[];
    window: TimeWindow | null;
    /** An object.* category; null for every object. */
    objectCategory: string | null;
    /** An event.* category; null for every event. */
    eventCategory: string | null;
    /** An EventSeverity; the severity filter writes it as a string. */
    minSeverity: string;
}
